// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * cm-coding: Global administrators must understand when each analysis prompt
 * rule applies before changing text used across applications. Show a plain
 * description and condition for every knowledge-base, page-mode, and diff-mode
 * rule. Render them from one configuration list so a rule cannot lose its usage
 * warning or present a different editing contract from its peers.
 */
import { useEffect, useState, type FC } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Textarea } from '@/components/ui'
import { Loader2, Save } from 'lucide-react'
import { toast } from 'sonner'
import { useServices } from '@/app-shell/services'

interface AnalysisSettings {
  kbCitationRules: string | null
  pageMarkerRules: string | null
  pageModeRules: string | null
  diffModeRules: string | null
}

type RuleKey = keyof AnalysisSettings

interface RuleSpec {
  key: RuleKey
  apiField: string
  label: string
  trigger: string
  description: string
  rows: number
}

const RULES: RuleSpec[] = [
  {
    key: 'kbCitationRules',
    apiField: 'kb_citation_rules',
    label: 'KB Citation Rules',
    trigger: 'When knowledge base items are present',
    description: 'Tells the AI how to cite knowledge base items in its findings.',
    rows: 6,
  },
  {
    key: 'pageMarkerRules',
    apiField: 'page_marker_rules',
    label: 'Page Marker Rules',
    trigger: 'When knowledge base items are present',
    description: 'Tells the AI how to read page numbers from the markers in document text.',
    rows: 6,
  },
  {
    key: 'pageModeRules',
    apiField: 'page_mode_rules',
    label: 'Page Mode Rules',
    trigger: 'When page-at-a-time analysis is on',
    description: 'Tells the AI it is seeing one page of a larger document, with neighbors for context.',
    rows: 4,
  },
  {
    key: 'diffModeRules',
    apiField: 'diff_mode_rules',
    label: 'Diff Mode Rules',
    trigger: 'When comparing document versions',
    description: 'Tells the AI it is reading a unified diff of what changed between versions.',
    rows: 4,
  },
]

const ANALYSIS_SETTINGS_KEY = ['analysis-settings'] as const

const EMPTY_VALUES: Record<RuleKey, string> = {
  kbCitationRules: '',
  pageMarkerRules: '',
  pageModeRules: '',
  diffModeRules: '',
}

export const SystemPromptsPanel: FC = () => {
  const services = useServices()
  const client = services.getClient()
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery<AnalysisSettings>({
    queryKey: ANALYSIS_SETTINGS_KEY,
    queryFn: async () => {
      const response = await client.get<AnalysisSettings>('/admin/analysis-settings')
      return response.data
    },
    staleTime: 5 * 60_000,
  })

  const [values, setValues] = useState<Record<RuleKey, string>>(EMPTY_VALUES)

  useEffect(() => {
    if (!data) return
    setValues({
      kbCitationRules: data.kbCitationRules ?? '',
      pageMarkerRules: data.pageMarkerRules ?? '',
      pageModeRules: data.pageModeRules ?? '',
      diffModeRules: data.diffModeRules ?? '',
    })
  }, [data])

  const saveMutation = useMutation({
    mutationFn: async () => {
      await client.put(
        '/admin/analysis-settings',
        Object.fromEntries(RULES.map(rule => [rule.apiField, values[rule.key].trim() || null])),
      )
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ANALYSIS_SETTINGS_KEY })
      toast.success('System prompts saved')
    },
    onError: (err) => {
      toast.error('Failed to save system prompts', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 rounded-xl border border-[#e7ecf2] bg-white px-6 py-12 shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        <span className="text-[13px] text-slate-500">Loading system prompts…</span>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-xl border border-[#e7ecf2] bg-white shadow-[0_1px_2px_rgba(2,8,23,0.04)]">
      {RULES.map((rule) => (
        <div key={rule.key} className="border-b border-[#eef2f6] px-6 py-5">
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor={rule.apiField} className="text-sm font-semibold tracking-[-0.01em]">
              {rule.label}
            </label>
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-[3px] text-[10.5px] font-[600] text-slate-500">
              {rule.trigger}
            </span>
          </div>
          <p className="mt-1 text-[12.5px] text-slate-500">{rule.description}</p>
          <Textarea
            id={rule.apiField}
            value={values[rule.key]}
            onChange={e => setValues(prev => ({ ...prev, [rule.key]: e.target.value }))}
            rows={rule.rows}
            className="mt-2.5 font-mono text-sm"
            placeholder="Leave blank to use default rules."
          />
        </div>
      ))}
      <div className="flex justify-end bg-[#fafbfc] px-6 py-4">
        <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
          {saveMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Save
        </Button>
      </div>
    </div>
  )
}
